import React, { useRef, useMemo, useState } from 'react';
import { Drawer, notification, Spin, Tooltip } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { cloneDeep, debounce, trim, filter } from 'lodash';
import moment from 'moment';

import AgGrid from "../../../../../../../components/ag-grid-table/index";
import Input from "../../../../../../../components/uiComponent/inputs/input/index"
import SelectInput from '../../../../../../../components/uiComponent/inputs/selectableInput';
import Button from "../../../../../../../components/uiComponent/button/index";
import LeftArrow from '../../../../../../../assets/icons/arrow-left-blue.svg';
import BarCodeScanner from '../../../../../../../assets/icons/bar-code-scan-icon.svg';
import ProductImage from '../../../../../../../assets/icons/product-image.svg';

import { CreateMultiSKUBoxWrapper } from '../../style'
import { CreateMultiSkuBox, GetWarehouseShipments } from '../../../../../../../slices/post-shipment';
import mix2DLablePdf from '../../../../../../../actions/utils/mix2DLablePdf';
import getBoxIndex from '../../../../../../../utils/get-box-index';

const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
  suppressMovable: true,
};

let gridApi;

const MultiSkuDrawer = ({
  setCreateMultiSKU,
  isCreateMultiSKU,
  toBeBoxed,
  activeShipmentIndex,
  boxesList
}) => {
  const { workOrderId } = useParams();
  const inputRef = useRef(null);
  const dispatch = useDispatch();

  const [filteredItems, setFilteredItems] = useState(toBeBoxed);
  const [itemsDetail, setItemsDetail] = useState(toBeBoxed?.map(({ id: shipment_item_id, sku_id, fnsku }) => ({ shipment_item_id, pack_quantity: 0, is_selected: false, sku_id, fnsku })) || []);
  const [dimensions, setDimensions] = useState({
    height: 5,
    width: 5,
    length: 5,
    weight: 5,
    unit: 'lb'
  });

  const {
    creatingMultiSkuBox,
    shipmentDetails,
  } = useSelector((state) => state.postShipment || {});

  const {
    amazon_shipment_id: amazonShipmentId,
    amazon_shipment_confirmation_id: shipmentId,
    name: shipmentName,

    amazon_warehouse_id: FCID,
    destination_name,
    destination_city,
    destination_country_code,
    destination_postal_code,
    destination_address_line_1,
    destination_state_or_province_code,

    source_city,
    source_country_code,
    source_postal_code,
    source_name,
    source_address_line_1,
  } = shipmentDetails?.shipments?.[activeShipmentIndex] || {};

  const handleInput = ({ e, shipment_item_id, availableQuantity }) => {
    if (!e.target.value) {
      setItemsDetail(itemsDetail => {
        const itemsDetailClone = cloneDeep(itemsDetail);

        const index = itemsDetailClone.findIndex(item => item.shipment_item_id === shipment_item_id);
        itemsDetailClone[index].pack_quantity = 0;
        itemsDetailClone[index].bundleQuantities = [];
        return itemsDetailClone;
      });
      return;
    }
    const quantity = Number(e.target.value);

    if (!quantity || isNaN(quantity) || (quantity < 0)) {
      return notification.warning({
        message: 'Please enter a valid pack quantity!',
        top: 60
      });
    }

    if (availableQuantity < quantity) {
      return notification.error({
        message: 'Pack quantity should be less than or equal to available quantity',
        top: 65
      });
    }

    setItemsDetail(itemsDetail => {
      const itemsDetailClone = cloneDeep(itemsDetail);

      const index = itemsDetailClone.findIndex(item => item.shipment_item_id === shipment_item_id);
      itemsDetailClone[index].pack_quantity = quantity;
      itemsDetailClone[index].is_selected = true;

      return itemsDetailClone;
    });

    onSelectionChange();
  };

  const gridReady = (params) => {
    gridApi = params.api;
  };

  const columnDefs = useMemo(() => [
    {
      width: 35,
      headerCheckboxSelection: true,
      checkboxSelection: true
    },
    {
      headerName: "Title",
      sortable: true,
      flex: 1,
      filter: true,
      cellRenderer: ({ data }) => {
        const { image_url, item_name } = data || {};

        return (
          <div className='title-ui-wrapper'>
            <img src={image_url || ProductImage} alt="Product-image" />
            <Tooltip placement='top' title={item_name || ''}>
              <p>
                {item_name || '---'}
              </p>
            </Tooltip>
          </div>
        )
      }
    },
    {
      headerName: 'SKU',
      field: 'msku',
      sortable: true,
      flex: 1,
      filter: true,
    },
    {
      headerName: 'FNSKU',
      field: 'fnsku',
      sortable: true,
      flex: 1,
      filter: true,
    },
    {
      headerName: 'Available Qty',
      sortable: true,
      filter: true,
      flex: 1,
      cellRenderer: ({ data }) => {
        const { quantity, boxed_quantity = 0 } = data || {};
        return (Number(quantity) - boxed_quantity)
      }
    },
    {
      headerName: 'Bundle Qty',
      sortable: true,
      filter: true,
      flex: 1,
      field: 'pack_quantity',
      cellRenderer: ({ data, value }) => {
        const { quantity, boxed_quantity = 0, id: shipment_item_id, product_details = [] } = data || {};
        const availableQuantity = Number(quantity) - boxed_quantity;
        const bundleQuantities = (product_details || []).map(item => item.bundle_quantity);
        let packQty = 0;
        setItemsDetail(itemsDetail => {
          bundleQuantities,
            packQty = itemsDetail.find(item => item.shipment_item_id === shipment_item_id)?.pack_quantity;
          return itemsDetail;
        });

        return (
          <div className='input-wrapper'>
            <Input
              placeholder="Enter"
              type="number"
              defaultValue={value || packQty || ''}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleInput({ e, shipment_item_id, availableQuantity });
              }}
              onBlur={(e) => handleInput({ e, shipment_item_id, availableQuantity })}
            />
          </div>
        )
      }
    },
  ], [filteredItems]);

  const handleGenerateMix2DBoxLabel = (boxesList, boxIndex) => {
    const items = itemsDetail
    .filter(({ pack_quantity }) => pack_quantity)
    .map(({ shipment_item_id, pack_quantity }) => {
      const { fnsku, expiration } = toBeBoxed.find(({ id }) => id === shipment_item_id) || {};
  
      let exp = null;
      if (expiration) {
        exp = moment(expiration, "YYYY-MM-DD").format("YYMMDD");
      }
  
      return { fnsku, qty: pack_quantity, exp };
    });


    const ship = {
      warehouse_name: source_name,
      address_line1: source_address_line_1,
      city: source_city,
      state_or_province_code: source_postal_code,
      country_code: source_country_code,

      ship_to_name: destination_name,
      destination_fulfillment_center_id: FCID,
      ship_to_address1: destination_address_line_1,
      ship_to_city: destination_city,
      ship_to_state: destination_state_or_province_code,
      ship_to_postal_code: destination_postal_code
    };

    mix2DLablePdf({
      shipmentId,
      userName: 'Opulent',
      shipmentName,
      dateCreated: moment().format("DD/MM/YY"),
      outbound_shipment_id: amazonShipmentId,
      items,
      numberOfBox: 1,
      PageStartIndex: 0,
      is2D: false,
      ship,
      boxIndex: boxIndex === -1 ? (boxesList?.length + 1) : (boxIndex + 1) // if box index is -1 then it means new box is created
    });
  };

  const handleCreateMultiSkuBox = () => {
    dispatch(CreateMultiSkuBox({
      itemsDetail: itemsDetail?.filter(({ is_selected }) => is_selected)?.map(({ shipment_item_id, pack_quantity, sku_id, fnsku }) => ({
        shipment_item_id,
        pack_quantity,
        sku_id,
        fnsku
      })),
      dimensions: {
        ...dimensions,
        weight: {
          value: dimensions?.weight,
          unit: dimensions?.unit || 'LB'
        }
      },
      amazonShipmentId,
      workOrderId: Number(workOrderId),
    }))
      .then(({ payload }) => {
        if (payload?.success) {
          setCreateMultiSKU(false);
          dispatch(GetWarehouseShipments({
            workOrderId,
          })).then(() => {
            const boxId = payload?.boxId;
            const boxIndex = getBoxIndex([], boxId, shipmentDetails?.shipments);
            handleGenerateMix2DBoxLabel(boxesList, boxIndex);
          })
        }
      });
  };

  const handleSearch = debounce(({ target }) => {
    const { value } = target || {};
    if (trim(value)) {
      setFilteredItems(
        filter(toBeBoxed, item => item.fnsku.toLowerCase().includes(value.toLowerCase()))
          .map((entry) => ({ ...entry, pack_quantity: itemsDetail.find(item => item.shipment_item_id === entry.id)?.pack_quantity || 0 }))
      );
    }
    else {
      setFilteredItems(
        toBeBoxed.map((entry) => ({ ...entry, pack_quantity: itemsDetail.find(item => item.shipment_item_id === entry.id)?.pack_quantity || 0 }))
      );
    }
    onSelectionChange();
  }, 800);

  const getCreateBtnDisabledStatus = () => {
    const { height, width, length, weight, unit } = dimensions;

    return (
      !height || isNaN(height) || Number(height) <= 0 ||
      !width || isNaN(width) || Number(width) <= 0 ||
      !length || isNaN(length) || Number(length) <= 0 ||
      !weight || isNaN(weight) || Number(weight) <= 0 ||
      !unit || creatingMultiSkuBox || !itemsDetail?.filter(({ is_selected }) => is_selected).length ||
      itemsDetail.some(({ pack_quantity, is_selected }) => is_selected && !pack_quantity)
    );
  };

  const onSelectionChange = (e) => {
    if (!e) {
      setItemsDetail(itemsDetail => {
        itemsDetail?.forEach(({ shipment_item_id, is_selected }) => {
          const node = gridApi.getRowNode(shipment_item_id);
          if (node && is_selected) node.setSelected(true);
        });
        return itemsDetail;
      });
    }
    else {
      const selectedRows = gridApi.getSelectedRows();
      const selectedRowIds = selectedRows.map(({ id }) => id);
      const filteredItemIds = filteredItems.map(({ id }) => id);

      const data = (inputRef.current.value) ?
        itemsDetail?.map((obj) => ({ ...obj, is_selected: (!selectedRowIds.includes(obj.shipment_item_id) && !filteredItemIds.includes(obj.shipment_item_id)) ? obj.is_selected : selectedRowIds.includes(obj.shipment_item_id) })) :
        itemsDetail?.map((obj) => ({ ...obj, is_selected: selectedRowIds.includes(obj.shipment_item_id) }));

      setItemsDetail(data);
    }
  };

  const { height, width, length, weight, unit } = dimensions || {};

  return (
    <Drawer
      placement="right"
      closable={false}
      width={996}
      onClose={() => setCreateMultiSKU(false)}
      visible={isCreateMultiSKU}
    >
      <Spin tip='Loading...' wrapperClassName="loading-info-wrapper" spinning={creatingMultiSkuBox}>
        <CreateMultiSKUBoxWrapper>
          <header>
            <div className='items-wrapper'>
              <img onClick={() => setCreateMultiSKU(false)} src={LeftArrow} alt="LeftArrow" />
              <p>Create Multi SKU Box</p>
            </div>
            <Input
              inputRef={inputRef}
              className="input-overlay-wrapper"
              placeholder="Barcode Scanner"
              endIcon={<img src={BarCodeScanner} alt="BarCodeScanner" />}
              onChange={handleSearch}
            />
          </header>
          <div className='items-data-wrapper'>
            <p className='title'>Item’s Info.</p>
            <Spin tip='Loading...' spinning={false}>
              <AgGrid
                user_referrals={filteredItems}
                defaultColDef={defaultColDef}
                columnDefinitions={columnDefs}
                gridReady={gridReady}
                rowSelection='multiple'
                onSelectionChanged={onSelectionChange}
                rowHeight={64}
                headerHeight={36}
                height="430px"
                className="sku-table"
              />
            </Spin>
          </div>
          <div className='add-dimension-container'>
            <p>Add Dimension</p>
            <div className='items-wrapper'>
              <Input
                className="dimension-input"
                placeholder="Enter"
                label="Length"
                value={length}
                onChange={({ target }) => setDimensions(dimensions => ({ ...dimensions, length: target.value }))}
                endIcon="Inches"
              />
              <Input
                className="dimension-input"
                placeholder="Enter"
                label="Height"
                onChange={({ target }) => setDimensions(dimensions => ({ ...dimensions, height: target.value }))}
                value={height}
                endIcon="Inches"
              />
              <Input
                className="dimension-input"
                placeholder="Enter"
                label="Width"
                onChange={({ target }) => setDimensions(dimensions => ({ ...dimensions, width: target.value }))}
                value={width}
                endIcon="Inches"
              />
              <SelectInput
                defaultDomain={weight}
                defaultExtension={unit}
                onDomainChange={value => setDimensions(dimensions => ({ ...dimensions, weight: value }))}
                onExtensionChange={value => setDimensions(dimensions => ({ ...dimensions, unit: value }))}
                extensions={["kg", "lb",]}
              />
            </div>
          </div>
          <Button
            onClick={handleCreateMultiSkuBox}
            text="Create Box & Print Box Label"
            disabled={getCreateBtnDisabledStatus()}
          />
        </CreateMultiSKUBoxWrapper>
      </Spin>
    </Drawer>
  );
}

export default MultiSkuDrawer